@if (layoutService.settingsOpen()) {
  <div class="ts-backdrop" role="presentation" (click)="close()"></div>

  <aside class="ts-panel" role="dialog" aria-modal="true" aria-label="Settings" (click)="$event.stopPropagation()">
    <div class="ts-head">
      <div class="min-w-0">
        <h5 class="ts-title">Settings</h5>
        <p class="ts-sub">Theme · language · fonts · layout</p>
      </div>
      <button type="button" class="ts-close" aria-label="Close settings" (click)="close()">
        <i class="ri-close-line"></i>
      </button>
    </div>

    <div class="ts-body">
      <!-- Mode + Language (always visible) -->
      <section class="ts-sec">
        <p class="ts-sec-label">Mode</p>
        <div class="ts-seg">
          <button type="button" class="ts-seg-btn" [class.active]="theme().themeMode === 'light'" (click)="setMode('light')">
            <i class="ri-sun-line"></i> Light
          </button>
          <button type="button" class="ts-seg-btn" [class.active]="theme().themeMode === 'dark'" (click)="setMode('dark')">
            <i class="ri-moon-clear-line"></i> Dark
          </button>
        </div>
      </section>

      <section class="ts-sec">
        <p class="ts-sec-label">Language</p>
        <div class="ts-seg">
          @for (lang of settings.languages; track lang.code) {
            <button type="button" class="ts-seg-btn" [class.active]="settings.language() === lang.code" (click)="setLanguage(lang.code)">
              <img [src]="lang.flag" alt="" width="14" height="14" class="ts-flag" />
              {{ lang.code | uppercase }}
            </button>
          }
        </div>
      </section>

      <!-- Logo & favicon -->
      <div class="ts-acc" [class.open]="isOpen('logos')">
        <button type="button" class="ts-acc-btn" (click)="toggleSection('logos')">
          <span>Logo &amp; favicon</span>
          <i class="ri-arrow-down-s-line"></i>
        </button>
        @if (isOpen('logos')) {
          <div class="ts-acc-body">
            <p class="ts-field-label">Header logo</p>
            <div class="ts-logo-row">
              @for (url of logoPresets; track url) {
                <button type="button" class="ts-logo-pick" [class.active]="theme().logo === url" (click)="patch({ logo: url })">
                  <img [src]="url" alt="" />
                </button>
              }
            </div>
            <p class="ts-field-label">Favicon URL</p>
            <div class="ts-logo-row">
              @for (f of faviconPresets; track f.url) {
                <button type="button" class="ts-logo-pick" [class.active]="theme().favicon === f.url" [attr.title]="f.label" (click)="patch({ favicon: f.url })">
                  <img [src]="f.url" alt="" />
                </button>
              }
            </div>
            <p class="ts-hint">Changes the top bar logo. Favicon updates the browser tab (may need a moment).</p>
          </div>
        }
      </div>

      <!-- Theme colors -->
      <div class="ts-acc" [class.open]="isOpen('themeColors')">
        <button type="button" class="ts-acc-btn" (click)="toggleSection('themeColors')">
          <span>Theme colors</span>
          <i class="ri-arrow-down-s-line"></i>
        </button>
        @if (isOpen('themeColors')) {
          <div class="ts-acc-body">
            <p class="ts-field-label">
              Primary
              <span class="ts-hex">{{ theme().primary }}</span>
            </p>
            <div class="ts-colors">
              @for (c of colorPresets; track c.hex) {
                <button type="button" class="ts-swatch" [class.active]="isPrimaryActive(c.hex)" [style.background-color]="c.hex" [attr.title]="c.name" (click)="settings.setPrimary(c.hex)">
                  @if (isPrimaryActive(c.hex)) {
                    <i class="ri-check-line"></i>
                  }
                </button>
              }
              <label class="ts-swatch ts-custom-color">
                <input type="color" class="ts-color-input" [value]="theme().primary" (input)="onPrimary($event)" />
                <i class="ri-add-line"></i>
              </label>
            </div>

            <p class="ts-field-label mt-2">
              Secondary
              <span class="ts-hex">{{ theme().secondary }}</span>
            </p>
            <div class="ts-colors">
              @for (c of colorPresets; track c.hex) {
                <button type="button" class="ts-swatch" [class.active]="isSecondaryActive(c.hex)" [style.background-color]="c.hex" (click)="settings.setSecondary(c.hex)">
                  @if (isSecondaryActive(c.hex)) {
                    <i class="ri-check-line"></i>
                  }
                </button>
              }
              <label class="ts-swatch ts-custom-color">
                <input type="color" class="ts-color-input" [value]="theme().secondary" (input)="onSecondary($event)" />
                <i class="ri-add-line"></i>
              </label>
            </div>
          </div>
        }
      </div>

      <!-- Text colors -->
      <div class="ts-acc" [class.open]="isOpen('text')">
        <button type="button" class="ts-acc-btn" (click)="toggleSection('text')">
          <span>Text colors</span>
          <i class="ri-arrow-down-s-line"></i>
        </button>
        @if (isOpen('text')) {
          <div class="ts-acc-body">
            <div class="ts-color-fields">
              <label>
                <span>Primary text</span>
                <input type="color" [value]="theme().textPrimary" (input)="onColor('textPrimary', $event)" />
              </label>
              <label>
                <span>Label text</span>
                <input type="color" [value]="theme().textLabel" (input)="onColor('textLabel', $event)" />
              </label>
              <label>
                <span>Body text</span>
                <input type="color" [value]="theme().textBody" (input)="onColor('textBody', $event)" />
              </label>
            </div>
          </div>
        }
      </div>

      <!-- Buttons -->
      <div class="ts-acc" [class.open]="isOpen('buttons')">
        <button type="button" class="ts-acc-btn" (click)="toggleSection('buttons')">
          <span>Buttons</span>
          <i class="ri-arrow-down-s-line"></i>
        </button>
        @if (isOpen('buttons')) {
          <div class="ts-acc-body">
            <div class="ts-color-fields">
              <label>
                <span>Primary bg</span>
                <input type="color" [value]="theme().btnPrimaryBg" (input)="onColor('btnPrimaryBg', $event)" />
              </label>
              <label>
                <span>Primary text</span>
                <input type="color" [value]="theme().btnPrimaryText" (input)="onColor('btnPrimaryText', $event)" />
              </label>
              <label>
                <span>Primary border</span>
                <input type="color" [value]="theme().btnPrimaryBorder" (input)="onColor('btnPrimaryBorder', $event)" />
              </label>
              <label>
                <span>Secondary bg</span>
                <input type="color" [value]="theme().btnSecondaryBg" (input)="onColor('btnSecondaryBg', $event)" />
              </label>
              <label>
                <span>Secondary text</span>
                <input type="color" [value]="theme().btnSecondaryText" (input)="onColor('btnSecondaryText', $event)" />
              </label>
              <label>
                <span>Secondary border</span>
                <input type="color" [value]="theme().btnSecondaryBorder" (input)="onColor('btnSecondaryBorder', $event)" />
              </label>
            </div>
            <p class="ts-field-label mt-2">Button radius</p>
            <div class="ts-seg ts-seg-wrap">
              @for (r of radii; track r) {
                <button type="button" class="ts-seg-btn" [class.active]="theme().btnRadius === r" (click)="patch({ btnRadius: r })">
                  {{ r === '9999' ? 'pill' : r + 'px' }}
                </button>
              }
            </div>
            <div class="ts-preview-row mt-2">
              <button type="button" class="ti-btn ti-btn-primary-full !mb-0 !py-1.5 !px-3 !text-[0.75rem]">Primary</button>
              <button type="button" class="ti-btn ti-btn-secondary-full !mb-0 !py-1.5 !px-3 !text-[0.75rem]">Secondary</button>
            </div>
          </div>
        }
      </div>

      <!-- Form fields -->
      <div class="ts-acc" [class.open]="isOpen('forms')">
        <button type="button" class="ts-acc-btn" (click)="toggleSection('forms')">
          <span>Form fields</span>
          <i class="ri-arrow-down-s-line"></i>
        </button>
        @if (isOpen('forms')) {
          <div class="ts-acc-body">
            <div class="ts-color-fields">
              <label>
                <span>Field text</span>
                <input type="color" [value]="theme().formText" (input)="onColor('formText', $event)" />
              </label>
              <label>
                <span>Border</span>
                <input type="color" [value]="theme().formBorder" (input)="onColor('formBorder', $event)" />
              </label>
              <label>
                <span>Background</span>
                <input type="color" [value]="theme().formBg" (input)="onColor('formBg', $event)" />
              </label>
            </div>
            <p class="ts-field-label mt-2">Field radius</p>
            <div class="ts-seg ts-seg-wrap">
              @for (r of radii; track r) {
                <button type="button" class="ts-seg-btn" [class.active]="theme().formRadius === r" (click)="patch({ formRadius: r })">
                  {{ r === '9999' ? 'pill' : r + 'px' }}
                </button>
              }
            </div>
            <div class="mt-2">
              <label class="ts-preview-label" for="ts-preview-field">Preview field</label>
              <input id="ts-preview-field" type="text" class="form-control form-control-sm" placeholder="Preview field" />
            </div>
          </div>
        }
      </div>

      <!-- Header & footer -->
      <div class="ts-acc" [class.open]="isOpen('headerFooter')">
        <button type="button" class="ts-acc-btn" (click)="toggleSection('headerFooter')">
          <span>Header &amp; footer</span>
          <i class="ri-arrow-down-s-line"></i>
        </button>
        @if (isOpen('headerFooter')) {
          <div class="ts-acc-body">
            <p class="ts-field-label">Header style</p>
            <div class="ts-seg">
              @for (opt of menuStyles; track opt.value) {
                <button type="button" class="ts-seg-btn" [class.active]="theme().headerStyle === opt.value" (click)="patch({ headerStyle: opt.value })">
                  {{ opt.label }}
                </button>
              }
            </div>
            <div class="ts-color-fields mt-2">
              <label>
                <span>Header bg</span>
                <input type="color" [value]="theme().headerBg" (input)="onColor('headerBg', $event)" />
              </label>
              <label>
                <span>Header text</span>
                <input type="color" [value]="theme().headerText" (input)="onColor('headerText', $event)" />
              </label>
              <label>
                <span>Footer bg</span>
                <input type="color" [value]="theme().footerBg" (input)="onColor('footerBg', $event)" />
              </label>
              <label>
                <span>Footer text</span>
                <input type="color" [value]="theme().footerText" (input)="onColor('footerText', $event)" />
              </label>
            </div>
          </div>
        }
      </div>

      <!-- Body colors -->
      <div class="ts-acc" [class.open]="isOpen('body')">
        <button type="button" class="ts-acc-btn" (click)="toggleSection('body')">
          <span>Body colors</span>
          <i class="ri-arrow-down-s-line"></i>
        </button>
        @if (isOpen('body')) {
          <div class="ts-acc-body">
            <div class="ts-color-fields">
              <label>
                <span>Body bg</span>
                <input type="color" [value]="theme().bodyBg" (input)="onColor('bodyBg', $event)" />
              </label>
              <label>
                <span>Body text</span>
                <input type="color" [value]="theme().textBody" (input)="onColor('textBody', $event)" />
              </label>
            </div>
          </div>
        }
      </div>

      <!-- Fonts & typography -->
      <div class="ts-acc" [class.open]="isOpen('fonts')">
        <button type="button" class="ts-acc-btn" (click)="toggleSection('fonts')">
          <span>Fonts &amp; typography</span>
          <i class="ri-arrow-down-s-line"></i>
        </button>
        @if (isOpen('fonts')) {
          <div class="ts-acc-body">
            <p class="ts-field-label">Body font</p>
            <div class="ts-font-grid">
              @for (font of bodyFonts; track font) {
                <button type="button" class="ts-font" [class.active]="theme().fontBody === font" [style.font-family]="font + ', sans-serif'" (click)="setBodyFont(font)">
                  {{ font }}
                </button>
              }
            </div>
            <p class="ts-field-label mt-2">RTL font</p>
            <div class="ts-font-grid">
              @for (font of rtlFonts; track font) {
                <button type="button" class="ts-font" [class.active]="theme().fontRtl === font" [style.font-family]="font + ', sans-serif'" (click)="setRtlFont(font)">
                  {{ font }}
                </button>
              }
            </div>
            <p class="ts-hint">Used when Arabic (RTL) is active.</p>
            <p class="ts-field-label mt-2">Font size (page)</p>
            <div class="ts-seg ts-seg-wrap">
              @for (b of fontBases; track b) {
                <button type="button" class="ts-seg-btn" [class.active]="theme().fontBase === b" (click)="patch({ fontBase: b })">
                  {{ b }}%
                </button>
              }
            </div>
          </div>
        }
      </div>

      <!-- Layout extras -->
      <div class="ts-acc" [class.open]="isOpen('layout')">
        <button type="button" class="ts-acc-btn" (click)="toggleSection('layout')">
          <span>Layout extras</span>
          <i class="ri-arrow-down-s-line"></i>
        </button>
        @if (isOpen('layout')) {
          <div class="ts-acc-body">
            <p class="ts-field-label">Menu</p>
            <div class="ts-seg">
              @for (opt of menuStyles; track opt.value) {
                <button type="button" class="ts-seg-btn" [class.active]="theme().menuStyle === opt.value" (click)="patch({ menuStyle: opt.value })">
                  {{ opt.label }}
                </button>
              }
            </div>
            <p class="ts-field-label mt-2">Page style</p>
            <div class="ts-seg">
              @for (opt of pageStyles; track opt.value) {
                <button type="button" class="ts-seg-btn" [class.active]="theme().pageStyle === opt.value" (click)="patch({ pageStyle: opt.value })">
                  {{ opt.label }}
                </button>
              }
            </div>
          </div>
        }
      </div>

      <!-- Status + reset -->
      <section class="ts-status">
        <div class="ts-status-row">
          <span>app</span>
          <strong>Master Admin</strong>
        </div>
        <div class="ts-status-row">
          <span>lang</span>
          <strong>{{ settings.language() }} · {{ settings.direction() }}</strong>
        </div>
        <div class="ts-status-row">
          <span>Font</span>
          <strong>{{ theme().fontBody }} · {{ theme().fontBase }}%</strong>
        </div>
        <button type="button" class="ts-reset" (click)="reset()">
          <i class="ri-refresh-line"></i>
          Reset to config
        </button>
      </section>
    </div>
  </aside>
}
